// Authentication service - accepts JWT from OpsHub application

import { store } from 'services/state'
import { updateAllUserData } from 'services/api-catalog'
import { initApiGatewayClient, adminGroupName, userGroupName, apiGatewayClientWithCredentials } from 'services/api'
import jwt_decode from 'jwt-decode'
import _ from 'lodash'

/**
 * Build a mock admin JWT for local development.
 * jwt_decode only base64-decodes the payload — it does NOT verify the signature,
 * so a structurally valid but unsigned token works fine for local testing.
 */
function createMockAdminToken () {
  const b64url = obj =>
    btoa(JSON.stringify(obj))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')
  const header = b64url({ alg: 'HS256', typ: 'JWT' })
  const group = _.get(window, 'config.adminGroupName', 'SG_APP_AWSCognito_BO-APIHubIntegration_Admin')
  const payload = b64url({
    sub: 'local-dev-admin-001',
    email: 'admin@local.dev',
    'cognito:groups': [group],
    // exp capped to 23 hours so setTimeout doesn't overflow the 32-bit ms limit
    exp: Math.floor(Date.now() / 1000) + 82800
  })
  return `${header}.${payload}.local-dev-signature`
}

export function isAuthenticated () {
  return !!store.idToken
}

function getUserGroups () {
  if (!store.idToken) return []
  const decoded = jwt_decode(store.idToken)
  // Support both OpsHub group claim formats:
  // 1. custom:groups (Azure AD federated via OpsHub) - comes as string "[group1, group2]"
  // 2. cognito:groups (native Cognito) - comes as array
  let groups = decoded['custom:groups'] || decoded['cognito:groups'] || []
  if (typeof groups === 'string') {
    groups = groups.replace(/^\[|\]$/g, '').split(',').map(g => g.trim()).filter(Boolean)
  }
  return groups
}

export function getUser () {
  return store.user
}

export function isRegistered () {
  if (!store.idToken) return false
  const groups = getUserGroups()
  const adminGroup = adminGroupName || 'apihub-admin-group'
  const userGroup = userGroupName || 'apihub-user-group'
  return groups.includes(adminGroup) || groups.includes(userGroup)
}

export function isAdmin () {
  if (!store.idToken) return false
  const groups = getUserGroups()
  const adminGroup = adminGroupName || 'apihub-admin-group'
  return groups.includes(adminGroup)
}

export function getUserCognitoGroups () {
  return getUserGroups()
}

let logoutTimer

function getRemainingSessionTime (idToken) {
  const decoded = jwt_decode(idToken)
  return decoded.exp * 1000 - Date.now()
}

/**
 * On page load, check for token from:
 * 1. URL hash parameter (passed from OpsHub when opening API Hub)
 * 2. postMessage (if loaded in an iframe from OpsHub)
 * 3. localStorage (resuming a session)
 */
export function init () {
  initApiGatewayClient()

  const isInIframe = window.self !== window.top

  // LOCAL DEV BYPASS: when localDevBypass is set in config.js, skip OpsHub token flow
  // and auto-set a mock admin session so the app is usable without OpsHub.
  // Skip this bypass when loaded inside an iframe (e.g. from backoffice) so that the
  // postMessage auth flow always takes precedence.
  if (!isInIframe && _.get(window, 'config.localDevBypass') === true) {
    // Prefer a real JWT pasted into config.localDevToken; fall back to mock
    const token = _.get(window, 'config.localDevToken') || createMockAdminToken()
    setSession(token)
    return
  }

  // 1. Check URL hash for token from OpsHub
  let idToken = extractTokenFromUrl()

  // 2. Check localStorage for existing session
  if (!idToken) {
    try {
      idToken = window.localStorage.getItem('apihub_id_token')
    } catch (error) {
      console.error(error)
    }
  }

  if (idToken) {
    const diff = getRemainingSessionTime(idToken)
    if (diff > 0) {
      setSession(idToken)
    } else {
      clearSession()
    }
  }

  // 3. Listen for postMessage from OpsHub/Backoffice (iframe integration)
  window.addEventListener('message', handlePostMessage)

  // 4. Listen for OpsHub token refresh events
  window.addEventListener('tokenRefresh', handleTokenRefresh)

  // 5. If loaded inside an iframe (e.g. from backoffice), signal that we are ready
  //    to receive the auth token via postMessage.
  //    Use window.config.parentOrigin if set (production: https://opshub.foodthinkers.com,
  //    local dev: http://localhost:3000). Falls back to '*' only if not configured.
  if (isInIframe) {
    const rawOrigin = _.get(window, 'config.parentOrigin', '*')
    const parentOrigins = Array.isArray(rawOrigin) ? rawOrigin : [rawOrigin]
    console.log('[ApiHub-Iframe] Running inside iframe — sending IFRAME_READY to parent(s):', parentOrigins)
    parentOrigins.forEach(origin => window.parent.postMessage({ type: 'IFRAME_READY' }, origin))
  }
}

function extractTokenFromUrl () {
  let idToken = null
  try {
    const hash = window.location.hash
    if (hash) {
      hash.replace(/^#/, '').split('&').map(param => param.split('=')).forEach(param => {
        if (param[0] === 'id_token') idToken = param[1]
      })
      // Clean the URL after extracting token
      if (idToken) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search)
      }
    }
  } catch (error) {
    console.error('Error extracting token from URL:', error)
  }
  return idToken
}

function handlePostMessage (event) {
  // Only accept messages from the configured parent origin (backoffice).
  // Falls back to permissive check only if parentOrigin is not configured.
  const allowedOrigin = _.get(window, 'config.parentOrigin')
  const allowedOrigins = Array.isArray(allowedOrigin)
    ? allowedOrigin
    : allowedOrigin ? [allowedOrigin] : []
  if (allowedOrigins.length && !allowedOrigins.includes(event.origin)) {
    // Only warn for messages that look like they were intended for us (have a type we recognise).
    // Silently ignore webpack HMR / React DevTools / browser-internal messages.
    const msgType = event.data?.type
    if (msgType === 'AUTH_TOKEN' || msgType === 'IFRAME_READY') {
      console.warn('[ApiHub-Iframe] Rejected postMessage type', msgType, 'from', event.origin, '(expected one of', allowedOrigins.join(', ') + ')')
    }
    return
  }

  console.log('[ApiHub-Iframe] Received postMessage:', event.data?.type, 'from', event.origin)

  // Accept AUTH_TOKEN messages from OpsHub
  if (event.data && event.data.type === 'AUTH_TOKEN' && event.data.payload) {
    const { idToken } = event.data.payload
    if (idToken) {
      console.log('[ApiHub-Iframe] AUTH_TOKEN received — setting session')
      setSession(idToken)
    }
  }
}

function handleTokenRefresh (event) {
  if (event.detail && event.detail.idToken) {
    setSession(event.detail.idToken)
  }
}

async function setSession (idToken) {
  try {
    const decoded = jwt_decode(idToken)
    store.idToken = idToken
    store.user = { email: decoded.email }
    window.localStorage.setItem('apihub_id_token', idToken)

    // Set auto-logout timer
    clearTimeout(logoutTimer)
    const remaining = getRemainingSessionTime(idToken)
    logoutTimer = setTimeout(clearSession, remaining)

    // Register/update user in Customers table and ensure they have an API key.
    // This is called on every login but is idempotent — the backend only creates
    // a new row / key if one does not already exist.
    try {
      const client = await apiGatewayClientWithCredentials()
      await client.post('/signin', {}, {}, {})
    } catch (signinError) {
      // Non-fatal: log but don't block the user from browsing the portal.
      console.warn('POST /signin failed (user may not have an API key):', signinError)
    }

    updateAllUserData()
  } catch (error) {
    console.error('Error setting session:', error)
    clearSession()
  }
}

/**
 * Login is now handled by receiving token from OpsHub.
 * This function processes a token passed via URL hash.
 */
export function login () {
  return new Promise((resolve, reject) => {
    const idToken = extractTokenFromUrl()
    if (idToken) {
      setSession(idToken)
      resolve(idToken)
    } else {
      reject(new Error('No token found'))
    }
  })
}

export function logout () {
  clearSession()
}

function clearSession () {
  clearTimeout(logoutTimer)
  logoutTimer = undefined
  store.resetUserData()
  store.idToken = undefined
  window.localStorage.removeItem('apihub_id_token')
}
